// Note: Several mutation endpoints use GET instead of POST/PUT, as the legacy backend API contract accepts.
// The party-size change POSTs when it carries a payment, so card data never sits in a URL.
import { api } from '@core/api';
import { toGuaranteeAdjustment, type GuaranteeAdjustment } from '@features/booking/api/guaranteeAdjustment';

export interface ModifyTimePolicyResponse {
  canModify: boolean;
  cancellationPolicyHours: number;
}

export interface AvailableTimeSlot {
  hour: string;
  areaId: number;
  available: boolean;
  restrictionTypes: string[];
}

interface AvailableTimesResponse {
  data: AvailableTimeSlot[];
}

interface SubmitTimeChangeResponse {
  date: string;
}

export async function checkModifyTimePolicy(
  reservationId: number,
): Promise<ModifyTimePolicyResponse> {
  return api.get<ModifyTimePolicyResponse>('/api/get-modify-time-policy', {
    params: { reservation: reservationId },
  });
}

export async function fetchAvailableTimesForEdition(
  subdomain: string,
  reservationId: number,
): Promise<AvailableTimeSlot[]> {
  const response = await api.get<AvailableTimesResponse>(
    `/api/rest/v2/restaurants/${subdomain}/availabilities/availabletimesforedition`,
    { params: { reservationId } },
  );
  return response.data;
}

// --- Change Size ---

export interface PartySizeInfo {
  available: boolean;
  areaId: number;
}

interface AvailableSizesResponse {
  data: PartySizeInfo[];
}

export async function fetchAvailableSizesForEdition(
  subdomain: string,
  reservationId: number,
): Promise<PartySizeInfo[]> {
  const response = await api.get<AvailableSizesResponse>(
    `/api/rest/v2/restaurants/${subdomain}/availabilities/availablesizesforedition`,
    { params: { reservationId } },
  );
  return response.data;
}

/** What the size change would do to the guarantee. */
export async function fetchPartySizeQuote(
  reservationId: number,
  newPartySize: number,
): Promise<GuaranteeAdjustment | null> {
  const response = await api.get<{ guaranteeAdjustment?: unknown }>('/api/party-size-quote', {
    params: { reservation: reservationId, newPartySize },
  });
  return toGuaranteeAdjustment(response?.guaranteeAdjustment);
}

/** What the guest accepted (the quote's `newFee`) and, for a deposit top-up, pays (its `outstanding`), echoed as received. */
export interface AcceptedGuarantee {
  newFee: number;
  outstanding?: number;
  /** MercadoPago's card token, the payment_method_id resolved from its number and the identity document it was minted with; Stripe charges the stored card. */
  card?: { tokenMP: string; paymentMethod: string; identityDocument: string };
}

export async function submitSizeChange(
  reservationId: number,
  newPartySize: number,
  areaId: number,
  accepted?: AcceptedGuarantee,
): Promise<void> {
  const change = { reservation: reservationId, newPartySize, area: areaId };
  const acceptance = accepted === undefined ? {} : { acceptGuarantee: 1, quotedNewFee: accepted.newFee };
  if (accepted?.outstanding === undefined) {
    await api.get('/api/edit_reservation_partysize', { params: { ...change, ...acceptance } });
    return;
  }
  // A charge goes in a POST body: URLs are logged, and the backend reads the card from the body only.
  await api.post('/api/edit_reservation_partysize', { ...change, ...acceptance, quotedOutstanding: accepted.outstanding, ...accepted.card });
}

// --- Change Name ---

export async function submitNameChange(
  reservationId: number,
  guestName: string,
): Promise<string> {
  return api.get<string>('/api/save_reservation_guest_name', {
    params: { id: reservationId, guest_name: guestName },
  });
}

// --- Cancel Reservation ---

export async function cancelReservation(
  lang: string,
  reservationId: number,
): Promise<void> {
  await api.post(`/api/${lang}/user-cancel-reservation`, { resId: reservationId });
}

export async function markWontAttend(
  reservationId: number,
): Promise<void> {
  await api.get('/api/wont-attend', {
    params: { reservation: reservationId },
  });
}

// --- Change Time ---

export async function submitTimeChange(
  reservationId: number,
  newTime: string,
  areaId: number,
): Promise<SubmitTimeChangeResponse> {
  return api.get<SubmitTimeChangeResponse>('/api/edit-reservation-time', {
    params: { reservation: reservationId, newResTime: newTime, area: areaId },
  });
}
